import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { detectHqApex } from './hq/detect';
import { detectSatelliteApex } from './satellites/detect';
import '@/styles/global.css';

function eventHaystack(event: Sentry.Event): string {
  return [
    event.message,
    ...(event.exception?.values ?? []).flatMap((v) => [v.type, v.value]),
  ]
    .filter(Boolean)
    .join('\n');
}

function isClerkLoadFailure(event: Sentry.Event): boolean {
  // Client network / adblock / flaky connectivity loading Clerk's browser SDK —
  // not an app bug. Matches HARBOR-WEB-G (code="failed_to_load_clerk_js").
  const haystack = eventHaystack(event);
  return (
    /failed_to_load_clerk_js/i.test(haystack) ||
    /Failed to load Clerk/i.test(haystack) ||
    /failed to load script:.*clerk/i.test(haystack)
  );
}

function clerkBundleFrameName(value: string | undefined): boolean {
  if (!value) {
    return false;
  }
  return /@clerk\/clerk-js|clerk\.browser(?:\.js)?/i.test(value);
}

/** Runtime-only Sentry field; removed from Event types in @sentry/react v8+. */
function eventCulprit(event: Sentry.Event): string | undefined {
  const culprit = (event as { culprit?: unknown }).culprit;
  return typeof culprit === 'string' ? culprit : undefined;
}

function isClerkScriptParseError(event: Sentry.Event): boolean {
  // Bots and outdated JS engines fail to parse Clerk's hosted bundle
  // (HARBOR-WEB-18: SyntaxError: Unexpected token '(' in clerk.browser.js).
  const haystack = eventHaystack(event);
  const isParseError =
    /SyntaxError/i.test(haystack) || /Unexpected token/i.test(haystack);
  if (!isParseError) {
    return false;
  }
  if (clerkBundleFrameName(eventCulprit(event))) {
    return true;
  }
  const frames = (event.exception?.values ?? []).flatMap(
    (v) => v.stacktrace?.frames ?? [],
  );
  return frames.some((frame) =>
    clerkBundleFrameName(frame.filename ?? frame.abs_path),
  );
}

function isTransientNetworkFailure(event: Sentry.Event): boolean {
  // Offline tab / HTML error page parsed as JSON (HARBOR-WEB-11, HARBOR-WEB-16).
  const haystack = eventHaystack(event);
  return (
    /^Failed to fetch$/im.test(haystack) ||
    /TypeError: Failed to fetch/i.test(haystack) ||
    /Unexpected token ['"]?</.test(haystack) ||
    /unexpected HTML response/i.test(haystack)
  );
}

Sentry.init({
  dsn: 'https://585410fae748b287b6ec1d6a6873f0a8@o4508071568998400.ingest.us.sentry.io/4510808342265856',
  environment: import.meta.env.MODE, // 'development' or 'production'
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Clerk's hosted clerk.browser.js is not our code. Parse/runtime failures
  // there are bots, adblock, or unsupported engines (HARBOR-WEB-18).
  denyUrls: [/\/npm\/@clerk\/clerk-js(?:@[^/]+)?\/dist\/clerk\.browser/i],
  beforeSend(event) {
    if (
      isClerkLoadFailure(event) ||
      isClerkScriptParseError(event) ||
      isTransientNetworkFailure(event)
    ) {
      return null;
    }
    return event;
  },
});

function SentryFallback() {
  return (
    <div className='min-h-screen flex items-center justify-center p-6 bg-sky-light'>
      <div className='text-center max-w-md'>
        <h1 className='text-xl font-bold text-gray-900 mb-2'>Something went wrong</h1>
        <p className='text-gray-600 mb-4'>
          An unexpected error occurred. Please refresh the page and try again.
        </p>
        <button
          type='button'
          onClick={() => window.location.reload()}
          className='px-4 py-2 rounded-xl bg-primary text-white font-semibold hover:bg-primary/90'
        >
          Refresh
        </button>
      </div>
    </div>
  );
}

async function bootstrap() {
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
  const hqApex = detectHqApex();
  if (hqApex) {
    const { HqApp } = await import('./hq/app');
    root.render(
      <React.StrictMode>
        <Sentry.ErrorBoundary fallback={<SentryFallback />}>
          <HqApp />
        </Sentry.ErrorBoundary>
      </React.StrictMode>,
    );
    return;
  }

  const apex = detectSatelliteApex();

  if (apex) {
    const { SatelliteApp } = await import('./satellites/app');
    root.render(
      <React.StrictMode>
        <Sentry.ErrorBoundary fallback={<SentryFallback />}>
          <SatelliteApp apex={apex} />
        </Sentry.ErrorBoundary>
      </React.StrictMode>,
    );
    return;
  }

  const [{ RouterProvider }, { router }] = await Promise.all([
    import('@tanstack/react-router'),
    import('./router'),
  ]);
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<SentryFallback />}>
        <RouterProvider router={router} />
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
}

void bootstrap();
